import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { formatDateTimeWithZone } from '../utils/formatDateTime';

/**
 * The reason a deal's last sync attempt failed, and when it was attempted.
 *
 * Both detail surfaces used to render the error in a bare alert at the very
 * bottom of the page — hundreds of lines below the status badge that would
 * prompt anyone to look for it — and neither showed a timestamp at all. The
 * error persists until the next attempt, so "when" is what makes it
 * actionable: an hour old means something to fix, a month old usually doesn't.
 *
 * `last_synced` is the mapping row's updated_at, i.e. the last attempt, not
 * the last success — hence "Last attempted".
 *
 * Sizing follows the `cva` variant pattern the ui/ primitives use (see
 * alert.tsx, badge.tsx) rather than ad-hoc conditionals, so the compact form
 * used inside the HubSpot modal stays a named design-system state.
 */

const titleVariants = cva('', {
  variants: {
    size: { default: '', compact: 'text-sm' },
  },
  defaultVariants: { size: 'default' },
});

const descriptionVariants = cva('', {
  variants: {
    size: { default: '', compact: 'text-xs' },
  },
  defaultVariants: { size: 'default' },
});

interface SyncFailureAlertProps extends VariantProps<typeof descriptionVariants> {
  errorMessage?: string | null;
  lastSynced?: string | null;
  className?: string;
}

const SyncFailureAlert: React.FC<SyncFailureAlertProps> = ({
  errorMessage,
  lastSynced,
  className,
  size,
}) => (
  <Alert variant="destructive" className={className}>
    <AlertTitle className={cn(titleVariants({ size }))}>Sync failed</AlertTitle>
    <AlertDescription className={cn(descriptionVariants({ size }))}>
      {/* A failed row without a recorded reason still beats showing nothing. */}
      {/* Stripe/HubSpot messages can be long and unbroken, so they must wrap
          inside the alert rather than overflow it. */}
      <p className="whitespace-pre-wrap break-words">
        {errorMessage || 'No error detail was recorded for this attempt.'}
      </p>
      {lastSynced && (
        <p className="mt-1 opacity-80">
          Last attempted {formatDateTimeWithZone(lastSynced)}
        </p>
      )}
    </AlertDescription>
  </Alert>
);

export default SyncFailureAlert;
